import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useFilterSet } from 'hooks/useFilterSet';
import { useState } from 'react';
import { DIALOG_STEPS, DialogStep } from './constants';
import { Button } from 'components/Button';

interface DeleteDialogProps {
  open: boolean;
  onClose: () => void;
  setDialog: (dialog: DialogStep) => void;
  filterSetToDelete: string | null;
}

export function DeleteDialog({ open, onClose, setDialog, filterSetToDelete }: DeleteDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { filterSets, deleteFilterSet } = useFilterSet();

  const filterSet = filterSetToDelete ? filterSets.find(set => set.filterSetId === filterSetToDelete) : null;

  const handleDelete = async () => {
    if (!filterSetToDelete) return;

    setIsDeleting(true);
    try {
      const success = await deleteFilterSet(filterSetToDelete);
      if (success) {
        setDialog(DIALOG_STEPS.FILTER_SELECT);
      } else {
        console.error('Failed to delete filter set');
      }
    } catch (error) {
      console.error('Error deleting filter set:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setDialog(DIALOG_STEPS.FILTER_SELECT);
  };

  const handleClose = () => {
    setDialog(DIALOG_STEPS.FILTER_SELECT);
    onClose();
  };

  if (!filterSet) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage
          id="advanced-filter-set-delete-dialog.title"
          defaultMessage="Delete filter set {name}"
          values={{ name: filterSet.name }}
        />
        <Button aria-label="close" icon="Cross" onClick={handleClose} className="ml-auto" />
      </DialogTitle>
      <DialogContent className="flex justify-center">
        <Typography variant="body1">
          <FormattedMessage
            id="advanced-filter-set-delete-dialog.message"
            defaultMessage="This action is permanent and cannot be undone. The filter set that you have saved under '{name}' will be removed from the saved filter sets."
            values={{
              name: <strong>{filterSet.name}</strong>,
            }}
          />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button id="delete-filter-set-cancel-button" priority="secondary" onClick={handleCancel} disabled={isDeleting}>
          <FormattedMessage id="general.button.cancel" defaultMessage="Cancel" />
        </Button>
        <Button id="delete-filter-set-delete-button" variant="critical" onClick={handleDelete} disabled={isDeleting}>
          {isDeleting ? (
            <FormattedMessage id="advanced-filter-set-delete-dialog.deleting" defaultMessage="Deleting..." />
          ) : (
            <FormattedMessage id="advanced-filter-set-delete-dialog.deleteButton" defaultMessage="Delete filter set" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
